// src/pages/MobileGridApp.jsx
// - Por defecto la lista oculta "Ready for QC" y "Archived" (hasta que elijas un filtro concreto).
// - El texto del Status en el filtro y el badge de Status usan color pastel (tu var actual).
// - Estrella activable por fila (persistente en localStorage).
// - Mantiene endpoints y lógica existentes (mobile_update_row.php).
// - Topbar blanco y fondo de cada card con imagen.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const BASE = window.API_BASE || "https://backend.sansfrontieres-design.com";

const STATUS_OPTIONS = [
  "Not Assigned",
  "Assigned",
  "In Process",
  "In Hold",
  "Correction",
  "Job Ready",
  "Ready for QC",
  "Billing",
  "Payed",
  "Archived",
  "Pre-Archived",

];

// Opciones del selector de Status dentro de la tabla móvil.
const GRID_STATUS_OPTIONS = [
  "Not Assigned",
  "Assigned",
  "In Process",
  "In Hold",
  "Correction",
  "Ready for QC",
  "Billing",
  "Payed",
  "Archived",
  "Job Ready",
];


const CARD_STATUS_OPTIONS = [
  "Not Assigned",
  "Assigned",
  "In Process",
  "In Hold",
  "Correction",
  "Ready for QC",
  "Billing",
  "Payed",
  "Archived",
  "Job Ready",
  "Pre-Archived",

];

const TYPE_OPTIONS = [
  "Coax Asbuilt",
  "Coax Design",
  "Coax Bentley",
  "Route",
  "Route FTTP",
  "Fiber FTTP",
  "Fiber Design BAU",
  "Fiber Asbuilt BAU",
  "Fiber Correction To Pay",
  "Fiber Bentley",
  "Iris Splits",
  "Other Tasks",
];

const PROJECT_OPTIONS = [
  "Project-01",
  "Project-02",
  "Project-03",
  "Project-04",
  "Project-05",
  "Project-06",
  "Project-07",
  "Project-08",
];



/* ====================== Helpers anti-CORS ====================== */
function postViaHiddenForm(url, data) {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.name = "hf_" + Math.random().toString(36).slice(2);
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const form = document.createElement("form");
    form.method = "POST";
    form.action = url;
    form.target = iframe.name;

    Object.entries(data || {}).forEach(([k, v]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = k;
      input.value = v ?? "";
      form.appendChild(input);
    });

    document.body.appendChild(form);

    const clean = () => {
      try { form.remove(); } catch {}
      try { iframe.remove(); } catch {}
      resolve();
    };

    iframe.addEventListener("load", () => setTimeout(clean, 80));
    setTimeout(clean, 1200);
    form.submit();
  });
}

async function fireAndRefresh(url, payload, refreshFn, delayMs = 600) {
  await postViaHiddenForm(url, payload);
  await new Promise((r) => setTimeout(r, delayMs));
  if (typeof refreshFn === "function") refreshFn();
}

/* ====================== Utilidades ====================== */
function getJobId(row) {
  return (
    row?.job_id ?? row?.JobId ?? row?.jobid ?? row?.id ?? row?.JobID ?? row?.ID
  );
}
function safe(val, fallback = "—") {
  if (val === null || val === undefined || val === "") return fallback;
  return String(val);
}
function normStatus(row) {
  return String(row?.Status || row?.status || row?.jobsta || "").trim();
}

// Normaliza status para UI/colores ("", "-" => "Not Assigned")
function normStatusUi(row) {
  const s = normStatus(row);
  if (!s || s === "-") return "Not Assigned";
  return s;
}

// Colores pastel por Project-## (solo UI)
function projectPastelBg(project) {
  const p = String(project || "").trim();
  const map = {
    "Project-01": "#fde2e4",
    "Project-02": "#dbeafe",
    "Project-03": "#dcfce7",
    "Project-04": "#fae8ff",
    "Project-05": "#fff7ed",
    "Project-06": "#e0f2fe",
    "Project-07": "#fef9c3",
    "Project-08": "#e9d5ff",
  };
  return map[p] || "";
}

// Colores pastel por Type (solo UI)
function typePastelBg(type) {
  const t = String(type || "").trim();
  const map = {
    "Coax Asbuilt": "#c7f9cc",
    "Coax Design": "#ffd6a5",
    "Coax Bentley": "#d0bfff",
    "Route": "#bae6fd",
    "Route FTTP": "#fecdd3",
    "Fiber FTTP": "#a7f3d0",
    "Fiber Design BAU": "#fde68a",
    "Fiber Asbuilt BAU": "#ddd6fe",
    "Fiber Correction To Pay": "#fecaca",
    "Fiber Bentley": "#bfdbfe",
    "Iris Splits": "#fbcfe8",
    "Other Tasks": "#e5e7eb",
  };
  return map[t] || "";
}

// Fecha/hora SQL en zona Lima (YYYY-MM-DD HH:MM:SS)
function nowSqlLima() {
  try {
    const s = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "America/Lima",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date());
    return s.replace("T", " ");
  } catch {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
}

function getJobDateSub(row) {
  return (
    row?.jobdatesub ||
    row?.SubmissionDate ||
    row?.Submission ||
    row?.submission ||
    ""
  );
}


/* ====== Modal de edición en Portal (componente estable) ======
   IMPORTANTE: Esto va FUERA de MobileGridApp para evitar remount en cada tecleo.
   (Si se define dentro del componente, React lo trata como un componente nuevo
   en cada render y el focus/cursor se pierde en textareas.)
*/
function EditModalPortal({ edit, closeEdit, saveEdit, setEdit }) {
  if (!edit) return null;

  const onBackdropDown = (e) => {
    if (e.target === e.currentTarget) closeEdit();
  };

  return createPortal(
    <div className="sfd-backdrop" onMouseDown={onBackdropDown}>
      <div className="sfd-modal" role="dialog" aria-modal="true">
        <div style={{ fontWeight: 800, marginBottom: 8 }}>
          Editar #{getJobId(edit.job)}
        </div>

        <label className="label">User</label>
        <select
          className="input"
          value={edit.form.User}
          onChange={(e) =>
            setEdit((prev) => ({ ...prev, form: { ...prev.form, User: e.target.value } }))
          }
          autoFocus
        >
          <option value="">(vacío)</option>
          <option value="Marco">Marco</option>
          <option value="Naara">Naara</option>
          <option value="Pilar">Pilar</option>
          <option value="Alessandra">Alessandra</option>
          <option value="Elizet">Elizet</option>
          <option value="Nelson">Nelson</option>
          <option value="Ibet">Ibet</option>
        </select>

        <label className="label">Notes Admin</label>
        <textarea
          className="input"
          style={{ minHeight: 68 }}
          value={edit.form.NotesAdmin}
          onChange={(e) =>
            setEdit((prev) => ({ ...prev, form: { ...prev.form, NotesAdmin: e.target.value } }))
          }
        />

        <label className="label">Notes</label>
        <textarea
          className="input"
          style={{ minHeight: 68 }}
          value={edit.form.Notes}
          onChange={(e) =>
            setEdit((prev) => ({ ...prev, form: { ...prev.form, Notes: e.target.value } }))
          }
        />

        <label className="label">Status</label>
        <select
          className="select input"
          value={edit.form.Status}
          onChange={(e) =>
            setEdit((prev) => ({ ...prev, form: { ...prev.form, Status: e.target.value } }))
          }
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <label className="label">Type</label>
        <select
          className="select input"
          value={edit.form.Type}
          onChange={(e) =>
            setEdit((prev) => ({ ...prev, form: { ...prev.form, Type: e.target.value } }))
          }
        >
          <option value="">(vacío)</option>
          {TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        <div className="grid2">
          <div>
            <label className="label">Route Aerial</label>
            <input
              className="input"
              value={edit.form.RouteAerial}
              onChange={(e) =>
                setEdit((prev) => ({ ...prev, form: { ...prev.form, RouteAerial: e.target.value } }))
              }
            />
          </div>
          <div>
            <label className="label">Route UG</label>
            <input
              className="input"
              value={edit.form.RouteUG}
              onChange={(e) =>
                setEdit((prev) => ({ ...prev, form: { ...prev.form, RouteUG: e.target.value } }))
              }
            />
          </div>

          <div>
            <label className="label">Coax AE</label>
            <input
              className="input"
              value={edit.form.CoaxAE}
              onChange={(e) =>
                setEdit((prev) => ({ ...prev, form: { ...prev.form, CoaxAE: e.target.value } }))
              }
            />
          </div>
          <div>
            <label className="label">Coax UG</label>
            <input
              className="input"
              value={edit.form.CoaxUG}
              onChange={(e) =>
                setEdit((prev) => ({ ...prev, form: { ...prev.form, CoaxUG: e.target.value } }))
              }
            />
          </div>

          <div>
            <label className="label">Asb AE</label>
            <input
              className="input"
              value={edit.form.AsbAE}
              onChange={(e) =>
                setEdit((prev) => ({ ...prev, form: { ...prev.form, AsbAE: e.target.value } }))
              }
            />
          </div>
          <div>
            <label className="label">Asb UG</label>
            <input
              className="input"
              value={edit.form.AsbUG}
              onChange={(e) =>
                setEdit((prev) => ({ ...prev, form: { ...prev.form, AsbUG: e.target.value } }))
              }
            />
          </div>

          <div>
            <label className="label">Lode/Node</label>
            <input
              className="input"
              value={edit.form.Lode}
              onChange={(e) =>
                setEdit((prev) => ({ ...prev, form: { ...prev.form, Lode: e.target.value } }))
              }
            />
          </div>
          <div>
            <label className="label">HC/Amps</label>
            <input
              className="input"
              value={edit.form.HC}
              onChange={(e) =>
                setEdit((prev) => ({ ...prev, form: { ...prev.form, HC: e.target.value } }))
              }
            />
          </div>
        </div>

        <div className="rowBtns" style={{ marginTop: 12 }}>
          <button className="btn" onClick={closeEdit}>Cancelar</button>
          <button className="btn" onClick={saveEdit}>Guardar</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ====================== Componente ====================== */
export default function MobileGridApp() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  // 1er grid (cards): por defecto mostrar SOLO "In Process"
  const [statusFilter, setStatusFilter] = useState("In Process"); // "" => sin filtro (muestra todos)
  const [showSimpleGrid, setShowSimpleGrid] = useState(true); // tabla inicial; el botón alterna con las tarjetas
  const [gridSortKey, setGridSortKey] = useState("project"); // "status" | "user" | "type" | "project" | "submission"
  const [gridSortDir, setGridSortDir] = useState("asc"); // "asc" | "desc"

  const toggleGridSort = (key) => {
    setGridSortKey((prev) => {
      if (prev === key) {
        setGridSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setGridSortDir("asc");
      return key;
    });
  };

  const gridSortArrow = (key) =>
    gridSortKey === key ? (gridSortDir === "asc" ? " ▲" : " ▼") : "";
  const [edit, setEdit] = useState(null);
  const [toast, setToast] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [addJobId, setAddJobId] = useState("");
  const [showSessionBubble, setShowSessionBubble] = useState(false);
  const sessionWrapRef = useRef(null);
  const SHOW_SESSION_BUTTON = false; // ocultar pastilla blanca de sesión

  // Notificaciones por transiciones a Ready for QC
  const [qcQueue, setQcQueue] = useState([]);   // [{id, notes}]
  const [showQc, setShowQc] = useState(false);
  const prevStatusRef = useRef(new Map());      // jobId -> lastStatus

  // Tema (claro/oscuro) con persistencia
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("sfd_theme") || "light"; } catch { return "light"; }
  });
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try { localStorage.setItem("sfd_theme", theme); } catch {}
  }, [theme]);

  // ★ Estrellados (persistentes en localStorage)
  const [stars, setStars] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("sfd_starred") || "[]")); }
    catch { return new Set(); }
  });
  useEffect(() => {
    try { localStorage.setItem("sfd_starred", JSON.stringify([...stars])); } catch {}
  }, [stars]);
  function toggleStar(id) {
    const key = String(id);
    setStars(prev => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key); else s.add(key);
      return s;
    });
  }

  // Usuario logueado (mostrar la sesión real). Nota: en algunos casos `sfd_user`
  // se guarda como string plano ("Project-03") y NO como JSON; por eso no usamos
  // JSON.parse() a ciegas.
  function readSessionName() {
    try {
      const candidates = [
        localStorage.getItem("sfd_user"),
        sessionStorage.getItem("sfd_user"),
        localStorage.getItem("loggedWorker"),
        sessionStorage.getItem("loggedWorker"),
      ].filter(Boolean);

      for (const raw of candidates) {
        if (!raw) continue;
        // 1) intentar JSON solo si parece JSON
        const trimmed = String(raw).trim();
        const looksJson = trimmed.startsWith("{") || trimmed.startsWith("[") || trimmed.startsWith('"');
        if (looksJson) {
          try {
            const j = JSON.parse(trimmed);
            if (typeof j === "string" && j.trim()) return j.trim();
            const name = j?.user || j?.username || j?.name || j?.project || j?.session;
            if (name && String(name).trim()) return String(name).trim();
          } catch {
            // si falla, seguimos al fallback
          }
        }
        // 2) fallback: string plano
        const plain = trimmed.replace(/^"+|"+$/g, "").trim();
        if (plain) return plain;
      }
    } catch {}
    return "";
  }

  const [sessionName, setSessionName] = useState(() => readSessionName() || "");

  // Alias para el resto del código (evita error eslint no-undef)
  const loggedUser = sessionName;

  // En móvil, aseguramos que NO quede pegado "modo PC" de usos anteriores.
  useEffect(() => {
    try {
      localStorage.removeItem("sfd_force_desktop");
      sessionStorage.removeItem("sfd_force_desktop");
    } catch {}
  }, []);

  // si el login actualiza storage y navegamos sin recargar, reflejar el cambio
  useEffect(() => {
    setSessionName(readSessionName() || "");
    const onStorage = () => setSessionName(readSessionName() || "");
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Globo de sesión: cerrar al tocar fuera y auto-cerrar
  useEffect(() => {
    if (!showSessionBubble) return;
    const onDown = (e) => {
      const el = sessionWrapRef.current;
      if (el && !el.contains(e.target)) setShowSessionBubble(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    const t = setTimeout(() => setShowSessionBubble(false), 6000);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      clearTimeout(t);
    };
  }, [showSessionBubble]);

  async function refreshProjects() {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/get_projects.php?_=${Date.now()}`);
      const t = await r.text();
      let arr = [];
      try {
        const j = JSON.parse(t);
        if (Array.isArray(j?.data)) arr = j.data;
        else if (Array.isArray(j)) arr = j;
      } catch {}

      // Detectar TRANSICIONES a Ready for QC
      const prevMap = prevStatusRef.current;
      const newMap = new Map();
      const newEvents = [];

      for (const row of arr) {
        const id = getJobId(row);
        const st = normStatus(row);
        if (id != null) {
          if (prevMap.size > 0) {
            const pst = prevMap.get(String(id));
            if (st === "Ready for QC" && pst !== "Ready for QC") {
              newEvents.push({
                id: String(id),
                notes: row?.Notes || row?.notes || row?.jobnotes || "—",
              });
            }
          }
          newMap.set(String(id), st);
        }
      }
      prevStatusRef.current = newMap;

      if (newEvents.length) {
        setQcQueue((prev) => {
          const seen = new Set(prev.map((e) => e.id));
          const merged = [...prev];
          for (const ev of newEvents) if (!seen.has(ev.id)) merged.push(ev);
          return merged;
        });
        setShowQc(true);
      }

      setItems(arr);
    } catch {
      setToast("No se pudo cargar la lista. Reintenta.");
      setTimeout(() => setToast(""), 2000);
    } finally {
      setLoading(false);
    }
  }
  function openAddModal(){
    setAddJobId("");
    setShowAddModal(true);
  }
  function closeAddModal(){
    setShowAddModal(false);
    setAddJobId("");
  }

  async function addNewProjectWithJobId(rawJobId) {
    const user = (typeof loggedUser === "string" && loggedUser.trim()) ? loggedUser.trim() : "Project-01";
    let jobid = String(rawJobId || "").trim();
    if (jobid.startsWith("#")) jobid = jobid.slice(1).trim();
    if (!jobid) {
      setToast("Ingresa un Job Id");
      setTimeout(() => setToast(""), 1400);
      return;
    }

    const exists = (items || []).some((r) => String(getJobId(r) || "").trim().toLowerCase() === jobid.toLowerCase());
    if (exists) {
      setToast("Ese Job Id ya existe en el grid");
      setTimeout(() => setToast(""), 1600);
      return;
    }

    if (!window.confirm(`Agregar nuevo registro en Projects\n\nJobId: ${jobid}`)) return;
    const limaNow = nowSqlLima();

    // Optimista: mostrar arriba
    const newRow = {
      job_id: jobid,
      JobId: jobid,
      jobid,
      jobsta: "Not Assigned",
      jobuser: "",
      jobti: "",
      jobclo: "Opened EP",
      jobclodate: limaNow,
      jobdatesub: limaNow,
      jobpro: "",
      jobnotesadmin: "",
      jobnotes: "",
      Notes: "",
      Status: "Not Assigned",
      User: "",
      Type: "",
      Project: "",
    };
    setItems((cur) => [newRow, ...(cur || [])]);

    try {
      await fireAndRefresh(`${BASE}/mobile_update_row.php`, {
        jobid,
        user,
        jobsta: "Not Assigned",
        jobuser: "",
        jobti: "",
        jobclo: "Opened EP",
        jobclodate: limaNow,
        jobdatesub: limaNow,
        jobpro: "",
        jobnotesadmin: "",
        jobnotes: "",
      }, refreshProjects, 800);
      setToast("Registro agregado");
      closeAddModal();
    } catch {
      setToast("No se pudo agregar. Revisa la red.");
    } finally {
      setTimeout(() => setToast(""), 1500);
    }
  }


  useEffect(() => {
    refreshProjects();
    const t = setInterval(refreshProjects, 45000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    let list = items || [];
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((row) => {
        const blob = [
          getJobId(row),
          row?.User, row?.user, row?.jobuser,
          row?.Notes, row?.notes, row?.jobnotes,
          row?.Status, row?.status, row?.jobsta,
          row?.Type, row?.jobti,
          row?.Project, row?.jobpro, row?.jobproname, row?.jobproport,
          row?.["EP Status"], row?.EPStatus, row?.ep_status,
        ].filter(Boolean).join(" ").toLowerCase();
        return blob.includes(s);
      });
    }
    // Si el usuario eligió un filtro, lo respetamos (si está vacío, mostramos todos)
    if (statusFilter) {
      const st = statusFilter.toLowerCase();
      list = list.filter((row) => normStatus(row).toLowerCase() === st);
    }
return list;
  }, [items, q, statusFilter]);

  const activeGridRows = useMemo(() => {
    let list = items || [];
    // En la vista "grid simple" (tabla), mostrar SOLO estos status:
    // In Process, Assigned, Job Ready, Correction, Not Assigned
    const allowed = new Set(["in process", "assigned", "job ready", "correction", "not assigned", "pre-archived"]);
    // Nota: si jobsta viene como "", "-" lo tratamos como "Not Assigned"
    list = list.filter((row) => allowed.has(normStatusUi(row).toLowerCase()));

// Reusar búsqueda (q) para esta vista también
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((row) => {
        const blob = [
          getJobId(row),
          row?.User, row?.user, row?.jobuser,
          row?.Notes, row?.notes, row?.jobnotes,
          row?.Status, row?.status, row?.jobsta,
          row?.Type, row?.jobti,
          row?.Project, row?.jobpro, row?.jobproname, row?.jobproport,
          row?.["EP Status"], row?.EPStatus, row?.ep_status,
        ].filter(Boolean).join(" ").toLowerCase();
        return blob.includes(s);
      });
    }
    return list;
  }, [items, q]);

  const activeGridRowsSorted = useMemo(() => {
    const arr = Array.isArray(activeGridRows) ? [...activeGridRows] : [];
    if (!gridSortKey) return arr;

    const getStr = (v) => (v == null ? "" : String(v)).trim().toLowerCase();

    const parseSubmissionTime = (row) => {
      const raw = row?.SubmissionDate || row?.Submission || row?.jobdatesub;
      if (!raw) return 0;
      const s = String(raw).trim();

      // Try ISO-like
      const isoLike = s.includes(" ") ? s.replace(" ", "T") : s;
      const t1 = Date.parse(isoLike);
      if (!Number.isNaN(t1)) return t1;

      // Try "dd/mm/yyyy, hh:mm:ss" with optional am/pm and commas
      const m = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})[^0-9]*(\d{1,2}):(\d{2})(?::(\d{2}))?/);
      if (m) {
        const dd = parseInt(m[1], 10);
        const mm = parseInt(m[2], 10);
        const yyyy = parseInt(m[3], 10);
        const hh = parseInt(m[4], 10);
        const mi = parseInt(m[5], 10);
        const ss = parseInt(m[6] || "0", 10);
        const dt = new Date(yyyy, mm - 1, dd, hh, mi, ss);
        return dt.getTime();
      }
      return 0;
    };

    const cmp = (a, b) => {
      if (gridSortKey === "submission") {
        const ta = parseSubmissionTime(a);
        const tb = parseSubmissionTime(b);
        return ta === tb ? 0 : ta < tb ? -1 : 1;
      }

      let va = "", vb = "";
      if (gridSortKey === "status") {
        va = getStr(a?.Status || a?.status || a?.jobsta);
        vb = getStr(b?.Status || b?.status || b?.jobsta);
      } else if (gridSortKey === "epstatus") {
        va = getStr(a?.["EP Status"] || a?.EPStatus || a?.ep_status || a?.jobclo);
        vb = getStr(b?.["EP Status"] || b?.EPStatus || b?.ep_status || b?.jobclo);
      } else if (gridSortKey === "user") {
        va = getStr(a?.User || a?.user || a?.jobuser);
        vb = getStr(b?.User || b?.user || b?.jobuser);
      } else if (gridSortKey === "type") {
        va = getStr(a?.Type || a?.jobti);
        vb = getStr(b?.Type || b?.jobti);
      } else if (gridSortKey === "project") {
        va = getStr(a?.Project || a?.jobpro);
        vb = getStr(b?.Project || b?.jobpro);
      }
      return va.localeCompare(vb);
    };

    arr.sort((a, b) => {
      const r = cmp(a, b);
      return gridSortDir === "asc" ? r : -r;
    });
    return arr;
  }, [activeGridRows, gridSortKey, gridSortDir]);


  /* ====== Acción: Ready for QC ====== */
  async function markReadyForQC(row) {
    const jobId = getJobId(row);
    if (!jobId) {
      setToast("Sin JobId");
      setTimeout(()=>setToast(""), 1200);
      return;
    }
    const user = typeof loggedUser === "string" ? loggedUser : (loggedUser?.user || "Project-01");

    // Optimista en UI
    setItems((cur) => cur.map((r) => (getJobId(r) === jobId ? { ...r, Status: "Ready for QC", jobsta: "Ready for QC" } : r)));
    try {
      await fireAndRefresh(`${BASE}/mobile_update_row.php`, {
        jobid: jobId, user,
        jobsta: "Ready for QC",
        jobuser: row?.User || row?.user || row?.jobuser || "",
        jobnotes: row?.Notes || row?.notes || row?.jobnotes || ""
      }, refreshProjects);
      setToast("Ready for QC enviado");
      setShowQc(true);
    } catch {
      setToast("No se pudo enviar. Revisa la red.");
    } finally {
      setTimeout(() => setToast(""), 1200);
    }
  }


  async function quickUpdateField(row, field, value) {
    const jobId = getJobId(row);
    if (!jobId) return;

    const user = typeof loggedUser === "string" ? loggedUser : (loggedUser?.user || "Project-01");
    const currentSub = getJobDateSub(row);
    const payload = {
      jobid: jobId,
      user,
      jobdatesub: currentSub || nowSqlLima(), // evita que el PHP cambie jobdatesub a NOW()
    };
    payload[field] = value;

    // Optimista local (solo UI)
    setItems((cur) =>
      (cur || []).map((r) => {
        if (getJobId(r) !== jobId) return r;
        if (field === "jobsta") return { ...r, Status: value, jobsta: value };
        if (field === "jobti") return { ...r, Type: value, jobti: value };
        if (field === "jobpro") return { ...r, Project: value, jobpro: value };
        return { ...r, [field]: value };
      })
    );

    try {
      await fireAndRefresh(`${BASE}/mobile_update_row.php`, payload, refreshProjects, 650);
    } catch {
      setToast("No se pudo guardar");
      setTimeout(() => setToast(""), 1400);
    }
  }

  /* ====== Edición básica ====== */
  function openEdit(row) {
    setEdit({
      job: row,
      form: {
        User: row?.User || row?.user || row?.jobuser || "",
        NotesAdmin: row?.["Notes Admin"] || row?.notesadmin || row?.jobnotesadmin || "",
        Notes: row?.Notes || row?.notes || row?.jobnotes || "",
        Status: row?.Status || row?.status || row?.jobsta || "",
        Type: row?.Type || row?.jobti || "",
        RouteAerial: row?.["Route Aerial"] || row?.routeaerial || row?.jobrouteaerial || "",
        RouteUG: row?.["Route UG"] || row?.routeug || row?.jobrouteug || "",
        CoaxAE: row?.["Coax AE"] || row?.coaxae || row?.jobcoaxae || "",
        CoaxUG: row?.["Coax UG"] || row?.coaxug || row?.jobcoaxug || "",
        AsbAE: row?.["Asb AE"] || row?.asbae || row?.jobasbae || "",
        AsbUG: row?.["Asb UG"] || row?.asbug || row?.jobasbug || "",
        Lode: row?.["Lode / Nodes"] || row?.lode || row?.joblode || "",
        HC: row?.["HC / Amps"] || row?.hc || row?.jobhc || "",
      },
    });
  }
  function closeEdit() { setEdit(null); }

  async function saveEdit() {
    if (!edit?.job) return;
    const jobId = getJobId(edit.job);
    const user = typeof loggedUser === "string" ? loggedUser : (loggedUser?.user || "Project-01");
    const payload = {
      jobid: jobId,
      user,
      jobuser: edit.form.User,
      jobnotesadmin: edit.form.NotesAdmin,
      jobnotes: edit.form.Notes,
      jobsta: edit.form.Status,
      jobti: edit.form.Type,

      jobrouteaerial: edit.form.RouteAerial,
      jobrouteug: edit.form.RouteUG,
      jobcoaxae: edit.form.CoaxAE,
      jobcoaxug: edit.form.CoaxUG,
      jobasbae: edit.form.AsbAE,
      jobasbug: edit.form.AsbUG,
      joblode: edit.form.Lode,
      jobhc: edit.form.HC,
    };
    // Optimista local
    setItems((cur) =>
      cur.map((r) =>
        getJobId(r) === jobId
          ? {
              ...r,
              User: edit.form.User,
              Notes: edit.form.Notes,
              Status: edit.form.Status,
              Type: edit.form.Type,

              // backend keys
              jobuser: edit.form.User,
              jobnotesadmin: edit.form.NotesAdmin,
              jobnotes: edit.form.Notes,
              jobsta: edit.form.Status,
              jobti: edit.form.Type,

              jobrouteaerial: edit.form.RouteAerial,
              jobrouteug: edit.form.RouteUG,
              jobcoaxae: edit.form.CoaxAE,
              jobcoaxug: edit.form.CoaxUG,
              jobasbae: edit.form.AsbAE,
              jobasbug: edit.form.AsbUG,
              joblode: edit.form.Lode,
              jobhc: edit.form.HC,
            }
          : r
      )
    );
    try {
      await fireAndRefresh(`${BASE}/mobile_update_row.php`, payload, refreshProjects);
      setToast("Guardado");
    } catch {
      setToast("Cambios locales. Servidor no confirmó.");
    } finally {
      setTimeout(() => setToast(""), 1500);
      closeEdit();
    }
  }

  function logout(){
    try {
      sessionStorage.removeItem("loggedWorker");
      localStorage.removeItem("loggedWorker");
      localStorage.removeItem("sfd_user");
    } catch {}
    try { window.location.href = "/"; } catch { window.location.reload(); }
  }

  function goToLoginDesktop(){
    // 🔑 Abrir versión PC desde el celular usando LA MISMA sesión actual
    // - Sin re-loguear
    // - Respeta el user activo (el de la pastilla/tooltip)
    // - Si el user es Drafter (ej: Project-03) abre DrafterGridPage; si no, GridPage

    const currentName = (sessionName || "").trim();

    // Detectar si es Drafter por nombre (Project-03) o por workfun/role si existe en loggedWorker
    let isDrafter = /\bproject-03\b/i.test(currentName);

    try {
      const rawLW =
        sessionStorage.getItem("loggedWorker") ||
        localStorage.getItem("loggedWorker") ||
        "";

      if (rawLW) {
        let lw = null;
        try { lw = JSON.parse(rawLW); } catch { lw = null; }
        if (lw) {
          const wf = String(lw.workfun || lw.work_fun || lw.role || lw.userrole || "").toLowerCase();
          if (wf.includes("draft")) isDrafter = true;
        } else {
          if (/draft/i.test(String(rawLW))) isDrafter = true;
        }
      }
    } catch {}

    // Forzar que la sesión que usa PC sea la misma que estás usando en mobile (evita quedarse en Project-01 viejo)
    try {
      if (currentName) {
        sessionStorage.setItem("sfd_user", currentName);
        localStorage.setItem("sfd_user", currentName);
      }
    } catch {}

    // Activar modo PC SOLO cuando tú tocas el botón
    try {
      localStorage.setItem("sfd_force_desktop", "1");
      sessionStorage.setItem("sfd_force_desktop", "1");
    } catch {}

    const dest = isDrafter ? "/?desktop=1#/drafter" : "/?desktop=1#/gridpage";

    try {
      window.location.replace(dest);
    } catch {
      try { window.location.href = dest; }
      catch { try { window.location.assign(dest); } catch {} }
    }
  }





  function clearQc(){
    setQcQueue([]);
    setShowQc(false);
  }

  function AddModalPortal() {
    if (!showAddModal) return null;
    const onBackdropDown = (e) => {
      if (e.target === e.currentTarget) closeAddModal();
    };
    const onAdd = () => addNewProjectWithJobId(addJobId);

    return createPortal(
      <div className="sfd-backdrop" onMouseDown={onBackdropDown}>
        <div className="sfd-modal" role="dialog" aria-modal="true">
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Agregar nuevo registro</div>

          <label className="label">Job Id</label>
          <input
            className="input"
            placeholder="Ej: JB0001234567"
            value={addJobId}
            onChange={(e) => setAddJobId(e.target.value)}
            autoFocus
          />

          <div className="rowBtns" style={{ marginTop: 12 }}>
            <button className="btn" onClick={closeAddModal}>Cancelar</button>
            <button className="btn" onClick={onAdd}>Agregar</button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  /* ====================== Render ====================== */
  return (
    <div className="mgrid-root">
      <style>{`
        :root{
          --bg:#f6f7f8;
          --card:#ffffff;
          --text:#1f2937;
          --muted:#475569;
          --border:#e5edf7;
          --shadow:rgba(65, 66, 68, 0.04);

          /* color pedido pastel (tu ajuste actual) */
          --status-color:#c7e5da;

          /* Neutros para botones genéricos */
          --btn-bg:#ffdab9;
          --btn-text:#1f2937;
          --btn-border:#e5edf7;

          /* Pasteles fijos (no cambian en dark) */
          --pastel-mint-bg:#fce1e4;   --pastel-mint-text:#515151; --pastel-mint-border:#eac4d5;
          --pastel-pink-bg:#b3dee2;   --pastel-pink-text:#515151; --pastel-pink-border:#a0ced9;
          --pastel-blue-bg:#fcf5c7;   --pastel-blue-text:#515151; --pastel-blue-border:#ffee93;

          /* Badges neutros */
          --badge-bg:#666666;
          --badge-border:#e3dcff;

          /* Estrella */
          --star:#f59e0b;
        }
        [data-theme="dark"]{
          --bg:#171718;
          --card:#393d42;
          --text:#e5e7eb;
          --muted:#94a3b8;
          --border:#1f2a44;
          --shadow:rgba(41, 39, 39, 0.5);

          --status-color:#c7e5da;

          --btn-bg:#666666;
          --btn-text:#e5e7eb;
          --btn-border:#22304a;

          --badge-bg:#515151;
          --badge-border:#2b3550;

          --star:#facc15;
        }

        [data-theme="dark"] .brand img{
          filter: drop-shadow(0 0 2px rgba(255,255,255,.35))
                  drop-shadow(0 0 8px rgba(255,255,255,.18))
                  brightness(1.25) saturate(1.15);
        }

        :where(html, body, #root){ margin:0; padding:0; width:100%; height:100%; background:var(--bg); color:var(--text); }
        .mgrid-root{ min-height:100vh; width:100vw; overflow-x:hidden; background:var(--bg); display:flex; flex-direction:column; }

        .topbar{
          display:flex;
          flex-wrap:wrap;
          align-items:center;
          gap:10px;
          padding:10px 12px;
          border-bottom:1px solid var(--border);
          background:#fff; /* blanco fijo */
          position:sticky; top:0; z-index: 10;
        }
        .topbar-left{
          display:flex;
          align-items:center;
          gap:10px;
          flex:1 1 auto;
          min-width: 220px;
        }
        .topbar-right{
          display:flex;
          align-items:center;
          justify-content:flex-end;
          gap:10px;
          flex:0 0 auto;
          flex-wrap:wrap;
        }
        [data-theme="dark"] .topbar{
          background:#e6e6e6ff;
          border-bottom:1px solid #e6e6e6ff;
        }

        .brand{ display:flex; align-items:center; gap:10px; font-weight:800; color:var(--text); }
        .brand img{ height:42px; object-fit:contain; filter: drop-shadow(0 1px 0 var(--shadow)); }

        .spacer{ flex:1 1 auto; }
        .bell-wrap{ position:relative; }
        .bell-badge{
          position:absolute; top:-6px; right:-6px; min-width:18px; height:18px; padding:0 5px;
          font-size:11px; background:#ef4444; color:#fff; border-radius:999px; display:grid; place-items:center;
          border:2px solid var(--card);
        }

        .toolbar{ display:flex; gap:8px; padding:12px; max-width:480px; width:100%; margin:0 auto; }
        .input, .select{
          width:100%; box-sizing:border-box; display:block;
          border:1px solid var(--border); border-radius:12px; padding:10px 12px; background:var(--card); font-size:14px; outline:none;
        }
        .input{ color:var(--text); }
        .input::placeholder{ color:var(--muted); }

        .select{ color:var(--text); }
        .select.has-status{ color:var(--status-color); }

        .list{ max-width:480px; width:100%; margin:6px auto 18px; padding:0 12px; display:grid; gap:10px; }
        .card{
          background:var(--card);
          background-image: linear-gradient(120deg, rgba(0,0,0,.28), rgba(0,0,0,.10)), url('/img/cell-bg.png');
          background-size: cover; background-repeat: no-repeat; background-position: center;
          border:1px solid var(--border); border-radius:16px; padding:12px; box-shadow: 0 2px 10px var(--shadow);
        }
        .row1{ display:flex; align-items:center; justify-content:space-between; gap:10px; }
        .row1-side{ display:flex; align-items:center; gap:8px; }
        .row2{ display:flex; flex-wrap:wrap; gap:6px; margin:8px 0 8px; }
        .badge{ display:inline-block; padding:4px 8px; background:var(--badge-bg); border:1px solid var(--badge-border); border-radius:10px; font-size:12px; }
        .badge.status{ color:var(--status-color); font-weight:800; font-size:15px; }


        .pillselect{
          display:inline-block;
          padding:4px 28px 4px 10px;
          border-radius:10px;
          border:1px solid var(--badge-border);
          background:var(--badge-bg);
          color:var(--text);
          font-size:12px;
          line-height:1.2;
          outline:none;
          appearance:none;
          -webkit-appearance:none;
          -moz-appearance:none;
          background-image:
            linear-gradient(45deg, transparent 50%, currentColor 50%),
            linear-gradient(135deg, currentColor 50%, transparent 50%),
            linear-gradient(to right, transparent, transparent);
          background-position:
            calc(100% - 14px) 55%,
            calc(100% - 9px) 55%,
            0 0;
          background-size: 5px 5px, 5px 5px, 100% 100%;
          background-repeat:no-repeat;
        }
        .pillstatic{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          padding:8px 12px;
          border-radius:999px;
          background: rgba(255,255,255,0.08);
          border:1px solid rgba(255,255,255,0.10);
          color: var(--text);
          font-weight:700;
          font-size:12px;
          line-height:1;
          white-space:nowrap;
          max-width: 44%;
          overflow:hidden;
          text-overflow:ellipsis;
        }

        .pillselect:focus{ box-shadow: 0 0 0 2px rgba(255,255,255,.08); }
        .pillselect.statuspill{ color:var(--status-color); font-weight:800; font-size:15px; }

        .meta{ color:var(--muted); font-size:14px; display:grid; gap:4px; margin-bottom:8px; }

        .jobid{
          display:inline-block; padding:6px 10px; border-radius:12px;
          background:var(--pastel-blue-bg); color:var(--pastel-blue-text); border:1px solid var(--pastel-blue-border);
          font-weight:800;
        }

        .actions{ display:flex; gap:8px; }
        .btn{
          flex:1; border-radius:12px; border:1px solid var(--btn-border); padding:10px 12px; background:var(--btn-bg);
          cursor:pointer; font-weight:700; color:var(--btn-text); transition:transform .04s ease, filter .12s ease;
        }
        .btn:hover{ filter:brightness(0.98); }
        .btn:active{ transform:translateY(1px); }
        .btn.ready{ background:var(--pastel-mint-bg); color:var(--pastel-mint-text); border-color:var(--pastel-mint-border); }
        .btn.edit{ background:var(--pastel-pink-bg); color:var(--pastel-pink-text); border-color:var(--pastel-pink-border); }

        .iconbtn{
          width:40px; height:36px; display:grid; place-items:center; border-radius:12px; border:1px solid var(--btn-border);
          background:var(--btn-bg); cursor:pointer; font-size:16px; color:var(--btn-text);
        }

        /* Sesión (globo)
        -------------------------------- */
        .session-wrap{ position:relative; display:flex; align-items:center; }
        .sessbtn{
          width:36px; height:36px; border-radius:999px;
          background:#fff; color:#111827; border:1px solid rgba(0,0,0,.18);
          font-weight:900; font-size:14px;
        }
        .sess-bubble{
          position:absolute; left:50%; top:44px; transform:translateX(-50%);
          background:#0b0b0c; color:#fff; border-radius:10px; padding:8px 10px;
          font-size:12px; line-height:1.15; min-width:120px; text-align:left;
          box-shadow: 0 10px 24px rgba(0,0,0,.30);
          border:1px solid rgba(255,255,255,.08);
          z-index:60;
        }
        .sess-bubble:before{
          content:""; position:absolute; top:-6px; left:50%; transform:translateX(-50%);
          width:0; height:0;
          border-left:6px solid transparent; border-right:6px solid transparent;
          border-bottom:6px solid #0b0b0c;
        }
        .sess-title{ font-weight:900; letter-spacing:.08em; opacity:.75; font-size:10px; }
        .sess-name{ font-weight:900; font-size:13px; margin-top:4px; }

        .starbtn{ width:36px; height:32px; font-size:18px; }
        .star-on{ color:var(--star); filter:saturate(1.2); }

        .toast{
          position:fixed; left:50%; transform:translateX(-50%); bottom:12px; z-index:30;
          background:var(--text); color:var(--card);
          padding:10px 14px; border-radius:10px; box-shadow: 0 12px 28px rgba(0,0,0,.25);
        }

        /* Modal */
        .sfd-backdrop{
          position:fixed; inset:0; background:rgba(15,23,42,.35);
          backdrop-filter: blur(2px); display:grid; place-items:center; z-index:1000;
        }
        .sfd-modal{
          background:var(--card); max-width:420px; width:92vw; border-radius:16px; border:1px solid var(--border); padding:16px 22px 16px 16px; max-height:86vh; overflow:auto; -webkit-overflow-scrolling:touch;
          box-shadow: 0 20px 50px var(--shadow); color:var(--text); position:relative; z-index:1001;
        }
        .label{ display:block; font-size:12px; color:var(--muted); margin:10px 0 6px; }
        .rowBtns{ display:flex; gap:8px; margin-top:12px; }

        .grid2{ display:grid; grid-template-columns: 1fr 1fr; gap:10px; margin-top:6px; }
        @media (max-width:360px){ .grid2{ grid-template-columns:1fr; } }

        /* Panel campanita estilo post-it */
        .qc-panel{
          position:fixed; right:12px; top:70px; width:320px; max-width:92vw; z-index:40;
          transform: rotate(1.2deg);
          background:#FFF3BF; color:#1a180f; border:1px solid #EFD780; border-radius:8px;
          box-shadow: 0 18px 40px rgba(0,0,0,.25);
        }
        .qc-head{ display:flex; align-items:center; justify-content:space-between; padding:8px 10px; font-weight:800; }
        .qc-actions{ display:flex; gap:6px; }
        .okbtn{
          padding:6px 10px; border-radius:10px; border:1px solid var(--btn-border);
          background:var(--btn-bg); color:var(--btn-text); font-weight:800; cursor:pointer;
        }
        .qc-body{ padding:8px 10px; max-height:50vh; overflow:auto; }
        .qc-item{ padding:6px 8px; border:1px dashed rgba(0,0,0,.08); border-radius:8px; background:rgba(255,255,255,.5); margin-bottom:6px; }
      
        /* ===== Simple horizontal grid view (sin botones) ===== */
        .simplewrap{
          max-width:480px; width:100%; box-sizing:border-box;
          margin:0 auto 18px; padding:0 8px; min-width:0;
          -webkit-text-size-adjust:100%;
        }
        .simplewrap .simplegrid-scroll{
          width:100%; max-width:100%; overflow-x:auto; overflow-y:hidden;
          -webkit-overflow-scrolling:touch;
        }
        .simplewrap .hint{
          color:var(--muted);
          font-size:13px;
          margin: 10px 2px 10px;
        }
        .simplegrid{
          width:max-content;
          min-width:100%;
          border-collapse:separate;
          border-spacing:0;
          border-radius:14px;
          overflow:hidden;
          background:#d4d4d4; /* gris mas oscuro */
        }
        .simplegrid th,
        .simplegrid td{
          border-right:1px solid #cfd6e3;
          border-bottom:1px solid #cfd6e3;
          padding:10px 12px;
          background:#d4d4d4; /* gris mas oscuro */
          color:#111;
          font-size:14.82px;
          font-weight:800;
          white-space:nowrap;
        }
        /* Tres columnas completas antes del desplazamiento horizontal. */
        .simplegrid th:nth-child(1), .simplegrid td:nth-child(1){
          box-sizing:border-box; width:138px; min-width:138px; max-width:138px;
          padding-left:6px; padding-right:6px; font-size:13.68px;
        }
        .simplegrid th:nth-child(2), .simplegrid td:nth-child(2){
          box-sizing:border-box; width:118px; min-width:118px; max-width:118px;
          padding-left:6px; padding-right:6px;
          text-align:center;
        }
        .simplegrid th:nth-child(3), .simplegrid td:nth-child(3){
          box-sizing:border-box; width:96px; min-width:96px; max-width:96px;
          padding-left:6px; padding-right:6px;
        }
        @media (max-width:480px){
          /* Ajuste proporcional para el texto mayor y tres columnas visibles. */
          .simplegrid th, .simplegrid td{
            font-size:clamp(14.82px, 3.819vw, 18.24px);
          }
          .simplegrid th:nth-child(1), .simplegrid td:nth-child(1){
            width:calc(40vw - 6px); min-width:calc(40vw - 6px); max-width:calc(40vw - 6px);
            font-size:clamp(13.68px, 3.534vw, 17.1px);
          }
          .simplegrid th:nth-child(2), .simplegrid td:nth-child(2){
            width:calc(35vw - 5px); min-width:calc(35vw - 5px); max-width:calc(35vw - 5px);
          }
          .simplegrid th:nth-child(3), .simplegrid td:nth-child(3){
            width:calc(25vw - 5px); min-width:calc(25vw - 5px); max-width:calc(25vw - 5px);
          }
        }
        .simplegrid thead th{
          position:sticky; top:0; z-index:2;
          background:#505554; /* turquesa oscuro en todos los encabezados */
          color:#F5F4C7;
          font-weight:800;
          text-transform:uppercase;
        }
        .simplegrid thead th:first-child{
          background:#505554;
        }
        .simplegrid tbody td:first-child{
          background:#fff3bf; /* JobId amarillo filas */
          font-weight:800;
        }
        .simplegrid tr:last-child td{ border-bottom:none; }
        .simplegrid td:last-child, .simplegrid th:last-child{ border-right:none; }

`}</style>

      <div className="topbar">
        <div className="topbar-left">
        <div className="brand">
          <img src={(process.env.PUBLIC_URL || "") + "/img/firma.png"} alt="SFD" />
        </div>
        <button className="iconbtn" onClick={goToLoginDesktop} title="Login" aria-label="Abrir Login">
          🔑
        </button>

        {SHOW_SESSION_BUTTON && (

        <div className="session-wrap" ref={sessionWrapRef}>
          <button
            className="iconbtn sessbtn"
            onClick={() => setShowSessionBubble((v) => !v)}
            title={sessionName || "Sesión"}
            aria-label="Ver sesión"
          >
            {(sessionName || "?").trim().charAt(0).toUpperCase()}
          </button>
          {showSessionBubble && (
            <div className="sess-bubble" role="tooltip">
              <div className="sess-title">SESIÓN</div>
              <div className="sess-name">{sessionName || "—"}</div>
            </div>
          )}
        </div>
        )}


        </div>
        <div className="topbar-right">
        <div className="bell-wrap">
          <button className="iconbtn" onClick={() => setShowQc(v => !v)} aria-label="Notificaciones">
            🔔
          </button>
          {qcQueue.length > 0 && <div className="bell-badge">{qcQueue.length}</div>}
        </div>

        <button className="iconbtn" onClick={() => setTheme(theme === "light" ? "dark" : "light")} title="Tema" aria-label="Cambiar tema">
          {theme === "dark" ? "☀️" : "🌙"}
        </button>
        <button className="iconbtn" onClick={refreshProjects} title="Refrescar" aria-label="Refrescar">↻</button>
        <button className="iconbtn" onClick={openAddModal} title="Agregar" aria-label="Agregar">＋</button>
        {/* Botón vista grid: SVG (más compatible que el caracter ▦ en algunos celulares) */}
        <button
          className={`iconbtn ${showSimpleGrid ? "active" : ""}`}
          onClick={() => setShowSimpleGrid((v) => !v)}
          title="Vista Grid"
          aria-label="Vista Grid"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" style={{ display: "block" }}>
            <rect x="2" y="2" width="4" height="4" rx="1" fill="currentColor" />
            <rect x="7" y="2" width="4" height="4" rx="1" fill="currentColor" />
            <rect x="12" y="2" width="4" height="4" rx="1" fill="currentColor" />
            <rect x="2" y="7" width="4" height="4" rx="1" fill="currentColor" />
            <rect x="7" y="7" width="4" height="4" rx="1" fill="currentColor" />
            <rect x="12" y="7" width="4" height="4" rx="1" fill="currentColor" />
            <rect x="2" y="12" width="4" height="4" rx="1" fill="currentColor" />
            <rect x="7" y="12" width="4" height="4" rx="1" fill="currentColor" />
            <rect x="12" y="12" width="4" height="4" rx="1" fill="currentColor" />
          </svg>
        </button>
        <button className="iconbtn" onClick={logout} title="Cerrar sesión" aria-label="Cerrar sesión">⎋</button>

        </div>
      </div>

      <div className="toolbar">
        <input className="input" placeholder="Buscar JobId, Notes, Name..." value={q} onChange={(e)=>setQ(e.target.value)} />
        <select
          className={`select ${statusFilter ? "has-status" : ""}`}
          value={statusFilter}
          onChange={(e)=>setStatusFilter(e.target.value)}
        >
          <option value="">Status (sin filtro)</option>
          <option>Not Assigned</option>
          <option>Assigned</option>
          <option>In Process</option>
          <option>In Hold</option>          
          <option>Correction</option>
          <option>Job Ready</option>
          <option>Ready for QC</option>
          <option>Billing</option>
          <option>Payed</option>
          <option>Pre-Archived</option>
          <option>Archived</option>
        </select>
      </div>

      {loading && <div className="list"><div className="card">Cargando…</div></div>}

      
      {showSimpleGrid ? (
        <div className="simplewrap">
          <div className="hint">Total records: {activeGridRowsSorted.length}</div>
          <style>{`.simplegrid th.simplegrid th.grid-darkcol,
        .simplegrid td.grid-darkcol{
          background:#2f3136 !important;
          color:#e6e6e6 !important;
          border-right:1px solid #3f434a !important;
          border-bottom:1px solid #3f434a !important;
        }
        /* Project pastel colors (vencer el !important del grid-darkcol usando CSS vars) */
        .simplegrid td.grid-darkcol.projectcell{
          background: var(--project-bg, #2f3136) !important;
          color: var(--project-fg, #e6e6e6) !important;
          font-weight: 800 !important;
        }
        /* Type pastel colors (vencer el !important del grid-darkcol usando CSS vars) */
        .simplegrid td.grid-darkcol.typecell{
          background: var(--type-bg, #2f3136) !important;
          color: var(--type-fg, #e6e6e6) !important;
          font-weight: 800 !important;
        }
        .simplegrid td.grid-darkcol.hotpriority{
          background:#f3a2a2 !important; /* rojo pastel */
          color:#1f2937 !important;
          font-weight:800 !important;
        }
`}</style>
            <div className="simplegrid-scroll">
            <table className="simplegrid">
              <thead>
                <tr>
                  <th className="grid-darkcol">Job Id</th>
                  <th onClick={() => toggleGridSort("status")} style={{ cursor: "pointer", userSelect: "none" }}>Status{gridSortArrow("status")}</th>
                  <th onClick={() => toggleGridSort("project")} style={{ cursor: "pointer", userSelect: "none" }}>Project{gridSortArrow("project")}</th>
                  <th onClick={() => toggleGridSort("type")} style={{ cursor: "pointer", userSelect: "none" }}>Type{gridSortArrow("type")}</th>
                  <th onClick={() => toggleGridSort("user")} style={{ cursor: "pointer", userSelect: "none" }}>User{gridSortArrow("user")}</th>
                  <th onClick={() => toggleGridSort("epstatus")} style={{ cursor: "pointer", userSelect: "none" }} className="grid-darkcol">EP Status{gridSortArrow("epstatus")}</th>
                  <th onClick={() => toggleGridSort("submission")} style={{ cursor: "pointer", userSelect: "none" }}>Submission{gridSortArrow("submission")}</th>
                  <th className="grid-darkcol">Notes</th>
                  <th className="grid-darkcol">NotesAdmin</th>
                </tr>
              </thead>
              <tbody>
                {activeGridRowsSorted.map((row) => {
                  const id = getJobId(row);
                  return (
                    <tr key={id || Math.random()}>
                      <td className="grid-darkcol">#{safe(id)}</td>
                      {(() => {
                        const statusUi = normStatusUi(row);
                        const statusBg =
                          statusUi === "In Process" ? "#fff3bf" :
                          statusUi === "Assigned" ? "#f8b1a7" :
                          statusUi === "Not Assigned" ? "#b7b7b7" :
                          statusUi.includes("Correction") ? "#f66e89" :
                          statusUi === "Job Ready" ? "#cf9ed5" :
                          statusUi === "In Hold" ? "#5a5955" :
                          "#726e6e";
                        return (
                          <td style={{ background: statusBg, fontWeight: 800, padding: 0 }}>
                            <select
                              aria-label={`Cambiar Status de ${id}`}
                              value={statusUi}
                              onChange={(e) => quickUpdateField(row, "jobsta", e.target.value)}
                              style={{
                                display: "block", width: "100%", minWidth: 0,
                                padding: "10px 2px", border: 0, cursor: "pointer",
                                background: "transparent", font: "inherit", fontSize: "clamp(13.68px, 3.534vw, 17.1px)", fontWeight: 800,
                                textAlign: "center", textAlignLast: "center",
                                color: statusUi === "In Hold" ? "#fff" : "#111",
                              }}
                            >
                              {!GRID_STATUS_OPTIONS.includes(statusUi) && (
                                <option value={statusUi}>{statusUi}</option>
                              )}
                              {GRID_STATUS_OPTIONS.map((status) => (
                                <option key={status} value={status} style={{ color: "#111", background: "#fff" }}>
                                  {status}
                                </option>
                              ))}
                            </select>
                          </td>
                        );
                                            })()}
                      {(() => {
                        const pr = safe(row?.Project || row?.jobpro, "");
                        const bg = projectPastelBg(pr);
                        // OJO: el grid usa background con !important, así que aquí usamos CSS vars
                        // (ver .projectcell en el <style> de simplegrid)
                        return (
                          <td
                            className="grid-darkcol projectcell"
                            style={{
                              "--project-bg": bg || "#2f3136",
                              "--project-fg": bg ? "#1f2937" : "#e6e6e6",
                            }}
                          >
                            {safe(pr)}
                          </td>
                        );
                      })()}
                      {(() => {
                        const ty = safe(row?.Type || row?.jobti, "");
                        const bg = typePastelBg(ty);
                        return (
                          <td
                            className="grid-darkcol typecell"
                            style={{
                              "--type-bg": bg || "#2f3136",
                              "--type-fg": bg ? "#1f2937" : "#e6e6e6",
                            }}
                          >
                            {safe(ty)}
                          </td>
                        );
                      })()}
                      <td className="grid-darkcol">{safe(row?.User || row?.user || row?.jobuser)}</td>
                      {(() => {
                        const epRaw = row?.EPStatus || row?.EP_Status || row?.epstatus || row?.jobclo || row?.EP || row?.ep;
                        const epStr = String(epRaw || "").trim();
                        const epClass = epStr === "HOT Priority" ? "grid-darkcol hotpriority" : "grid-darkcol";
                        return (
                          <td className={epClass}>
                            {safe(epRaw)}
                          </td>
                        );
                      })()}
                      <td className="grid-darkcol">{safe(row?.SubmissionDate || row?.Submission || row?.jobdatesub)}</td>
                      <td className="grid-darkcol" style={{ whiteSpace: "normal", minWidth: 260 }}>{safe(row?.Notes || row?.notes || row?.jobnotes)}</td>
                      <td className="grid-darkcol" style={{ whiteSpace: "normal", minWidth: 260 }}>{safe(row?.NotesAdmin || row?.notesadmin || row?.jobnotesadmin || row?.notes_admin || row?.Notes_Admin)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {!loading && activeGridRows.length === 0 && (
              <div style={{ textAlign: "center", color: "var(--muted)", padding: 24 }}>
                Sin resultados.
              </div>
            )}
          </div>
        </div>
      ) : (
<div className="list">
        {filtered.map((row) => {
          const id = getJobId(row);
          const starred = stars.has(String(id));
          return (
            <div key={id || Math.random()} className="card">
              <div className="row1">
                <div className="row1-side">
                  <span className="jobid">#{safe(id)}</span>
                </div>
                <div className="row1-side">
                  <button
                    className={`iconbtn starbtn ${starred ? "star-on" : ""}`}
                    onClick={() => toggleStar(id)}
                    aria-label={starred ? "Quitar estrella" : "Marcar con estrella"}
                    title={starred ? "Quitar estrella" : "Marcar con estrella"}
                  >
                    {starred ? "★" : "☆"}
                  </button>
                </div>
              </div>

              <div className="row2">
                <select
                  className="pillselect statuspill"
                  value={String(row?.Status || row?.jobsta || "").trim()}
                  onChange={(e) => quickUpdateField(row, "jobsta", e.target.value)}
                >
                  {CARD_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>

                <span className="pillstatic" title={safe(row?.Type || row?.jobti || "")}>
                  {safe(row?.Type || row?.jobti || "Type")}
                </span>

                <select
                  className="pillselect"
                  value={(row?.Project || row?.jobpro || "")}
                  onChange={(e) => quickUpdateField(row, "jobpro", e.target.value)}
                >
                  <option value="">Project</option>
                  {PROJECT_OPTIONS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>

                {row?.["EP Status"] || row?.EPStatus ? <span className="badge">{safe(row?.["EP Status"] || row?.EPStatus)}</span> : null}
              </div>

              <div className="meta">
                {(row?.User || row?.user || row?.jobuser) ? (
                <div><b style={{color:"var(--text)"}}>User:</b> {safe(row?.User || row?.user || row?.jobuser, "")}</div>
              ) : null}
                <div><b style={{color:"var(--text)"}}>Submission:</b> {safe(row?.SubmissionDate || row?.Submission || row?.jobdatesub)}</div>
                <div style={{wordBreak:"break-word"}}><b style={{color:"var(--text)"}}>Notes:</b> {safe(row?.Notes || row?.notes || row?.jobnotes)}</div>
              </div>

              <div className="actions">
                <button className="btn ready" onClick={() => markReadyForQC(row)}>● Ready for QC</button>
                <button className="btn edit" onClick={() => openEdit(row)}>✎ Editar</button>
              </div>
            </div>
          );
        })}

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: "center", color: "var(--muted)", padding: 24 }}>
            Sin resultados.
          </div>
        )}
      </div>

      
      )}

{/* Panel tipo post-it de la campanita */}
      {showQc && (
        <div className="qc-panel">
          <div className="qc-head">
            <span>🔔 Ready for QC ({qcQueue.length})</span>
            <div className="qc-actions">
              <button className="okbtn" onClick={clearQc} aria-label="OK">OK</button>
              <button className="iconbtn" onClick={() => setShowQc(false)} aria-label="Cerrar">✕</button>
            </div>
          </div>
          <div className="qc-body">
            {qcQueue.length === 0 ? (
              <div className="qc-item">No hay pendientes.</div>
            ) : (
              qcQueue.map((r) => (
                <div key={r.id} className="qc-item">
                  <div><b>Job:</b> #{safe(r.id)}</div>
                  <div><b>Notes:</b> {safe(r.notes, "—")}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Modal de edición (Portal) */}
      <EditModalPortal
        edit={edit}
        closeEdit={closeEdit}
        saveEdit={saveEdit}
        setEdit={setEdit}
        getJobId={getJobId}
      />

      {/* Modal de agregar (Portal) */}
      <AddModalPortal />

      {!!toast && <div className="toast">{toast}</div>}
    </div>
  );
}
